import React, { useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

import MenuIcon from "@mui/icons-material/Menu";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import LogoutIcon from "@mui/icons-material/Logout";

import { logout } from "../redux/actions/userActions";
import { Organizationlist } from "../redux/actions/OrganizationAction";
import { ORGANIZATION_DETAILS_SUCCESS } from "../redux/constants/OrganizationConstants";
import ConfirmDialog from "./common/ConfirmDialog";
import Logo from "../logo/Logo.svg";

const DRAWER_WIDTH = 240;

/** Best-effort 1–2 letter initials derived from a full name. */
function initialsFrom(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}

const Header = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const organizations = useSelector((s) => s.organizationList.organizations);
  const organization = useSelector((s) => s.organizationDetails.organization);
  const {
    userLogin: {
      userInfo: { data },
    },
  } = useSelector((s) => s);

  const [orgName, setOrgName] = useState("");
  const [userMenuEl, setUserMenuEl] = useState(null);
  const [orgBlockOpen, setOrgBlockOpen] = useState(false);
  const [userBlockOpen, setUserBlockOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const toggleSidebar = () => {
    const current = localStorage.getItem("sidebarVisible") === "true";
    localStorage.setItem("sidebarVisible", String(!current));
    window.dispatchEvent(new Event("storage"));
  };

  // Greeting
  const curHr = new Date().getHours();
  const greeting = curHr < 12 ? "Good Morning" : curHr < 17 ? "Good Afternoon" : "Good Evening";

  // Initial org hydration / block-state check (preserved from legacy)
  useEffect(() => {
    const pageReloaded = sessionStorage.getItem("pageReloaded");

    const checkOrganizationAndUserStatus = (response) => {
      if (response?.length > 0) {
        const firstOrg = response[0];
        if (firstOrg.organizationActive === 0) setOrgBlockOpen(true);
        if (firstOrg.userActive === 0) setOrgBlockOpen(true);
        if (firstOrg.userAction === 0) setUserBlockOpen(true);
        dispatch({ type: ORGANIZATION_DETAILS_SUCCESS, payload: firstOrg });
        setOrgName(firstOrg.name);
      }
    };

    if (pageReloaded) {
      dispatch(Organizationlist("", "", "", "", "Member")).then(checkOrganizationAndUserStatus);
    } else {
      if (organizations?.length === 0) {
        dispatch(Organizationlist("", "", "", "", "Member")).then((response) => {
          if (response?.length > 0) {
            dispatch({ type: ORGANIZATION_DETAILS_SUCCESS, payload: response[0] });
            setOrgName(response[0].name);
            if (response[0].organizationActive === 0) setOrgBlockOpen(true);
          }
        });
      } else if (!organization) {
        const firstOrg = organizations[0];
        if (firstOrg.organizationActive === 0) setOrgBlockOpen(true);
        else if (firstOrg.userActive === 0) setUserBlockOpen(true);
        dispatch({ type: ORGANIZATION_DETAILS_SUCCESS, payload: firstOrg });
        setOrgName(firstOrg.name);
      } else if (organizations?.length > 0) {
        const firstOrg = organizations[0];
        if (firstOrg.organizationActive === 0) setOrgBlockOpen(true);
        setOrgName(firstOrg.name);
      }
      sessionStorage.setItem("pageReloaded", "true");
    }

    const handleBeforeUnload = () => sessionStorage.setItem("pageReloaded", "true");
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      sessionStorage.removeItem("pageReloaded");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetData = (value) => {
    dispatch({ type: ORGANIZATION_DETAILS_SUCCESS, payload: value });
    if (value.organizationActive === 0) setOrgBlockOpen(true);
    else if (value.userActive === 0) setUserBlockOpen(true);
    else if (value.organization_id) history.push("/apps");
  };

  const openLogoutConfirm = () => {
    setUserMenuEl(null);
    setLogoutConfirmOpen(true);
  };

  const confirmLogout = () => {
    setLogoutConfirmOpen(false);
    dispatch(logout());
    history.push("/login");
  };

  const closeOrgBlock = () => {
    setOrgBlockOpen(false);
    const activeOrg = organizations?.find((o) => o.organizationActive === 1);
    if (activeOrg) handleSetData(activeOrg);
    else history.push("/login");
  };

  const closeUserBlock = () => {
    setUserBlockOpen(false);
    const activeUserOrg = organizations?.find((o) => o.userActive === 1);
    if (activeUserOrg) handleSetData(activeUserOrg);
    else history.push("/login");
  };

  const showOrgSelector = data?.role !== "Super_Admin" && organizations?.length > 0;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {/* Drawer toggle — visible on mobile via Sidebar's internal state too; on desktop it's a collapse toggle */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Toggle sidebar"
            onClick={toggleSidebar}
            sx={{ mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand */}
          <Box
            component={RouterLink}
            to="/apps"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              width: { xs: 100, md: 140 },
              mr: 2,
            }}
          >
            <Box component="img" src={Logo} alt="Beta Appstore" sx={{ height: 32, width: "auto" }} />
          </Box>

          {/* Greeting — hidden on xs to save space */}
          <Typography
            variant="h5"
            component="h1"
            sx={{
              display: { xs: "none", md: "block" },
              flexGrow: 1,
              fontSize: 18,
              letterSpacing: "-0.005em",
            }}
          >
            {greeting},{" "}
            <Box component="span" sx={{ color: "primary.dark", fontWeight: 800 }}>
              {data?.name}
            </Box>
          </Typography>
          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          {/* Right cluster: org selector + user menu */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {showOrgSelector && (
              <FormControl
                size="small"
                sx={{ minWidth: 160, maxWidth: 220, display: { xs: "none", sm: "inline-flex" } }}
              >
                <InputLabel id="organization-select-label">Organization</InputLabel>
                <Select
                  labelId="organization-select-label"
                  id="organization-select"
                  value={organization?.name || orgName}
                  label="Organization"
                >
                  {organizations.map((org, index) => (
                    <MenuItem
                      key={index}
                      value={org?.name}
                      onClick={() => handleSetData(org)}
                    >
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <IconButton
              onClick={(e) => setUserMenuEl(e.currentTarget)}
              aria-label="Open user menu"
              size="large"
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "primary.main",
                  color: "secondary.dark",
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: "0.02em",
                }}
              >
                {initialsFrom(data?.name)}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={userMenuEl}
              open={Boolean(userMenuEl)}
              onClose={() => setUserMenuEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { minWidth: 260, mt: 1 } }}
            >
              <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: "primary.main", color: "secondary.dark", fontWeight: 800 }}>
                    {initialsFrom(data?.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {data?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {data?.email}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Divider />

              <MenuItem
                component={RouterLink}
                to="/user-password"
                onClick={() => setUserMenuEl(null)}
              >
                <ListItemIcon>
                  <LockOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Update password
              </MenuItem>

              {data?.role !== "Super_Admin" && (
                <MenuItem
                  component={RouterLink}
                  to="/organization/create"
                  onClick={() => setUserMenuEl(null)}
                >
                  <ListItemIcon>
                    <AddBusinessIcon fontSize="small" />
                  </ListItemIcon>
                  Create organization
                </MenuItem>
              )}

              <Divider />

              <MenuItem onClick={openLogoutConfirm}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Spacer matching the fixed AppBar height so page content doesn't slide under it. */}
      <Toolbar />

      {/* Org block dialog */}
      <Dialog open={orgBlockOpen} onClose={closeOrgBlock} aria-labelledby="org-block-title">
        <DialogTitle id="org-block-title">Organization Blocked</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your organization <b>{orgName}</b> is blocked.
            <br />
            Please contact the administrator for further assistance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOrgBlock}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* User block dialog */}
      <Dialog open={userBlockOpen} onClose={closeUserBlock} aria-labelledby="user-block-title">
        <DialogTitle id="user-block-title">Member Blocked</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This user is currently blocked from accessing this organization.
            <br />
            Please reach out to your administrator for further assistance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUserBlock}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Logout confirmation */}
      <ConfirmDialog
        open={logoutConfirmOpen}
        title="Log out of Beta Appstore?"
        body="You will need to sign in again to continue."
        confirmLabel="Log out"
        confirmColor="primary"
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export { DRAWER_WIDTH };
export default Header;
